import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Plane } from "lucide-react";
import { useEffect, useRef, useState } from "react";

// Hill Country flat rates - Updated January 2026 (All-inclusive with gratuity)
const rates = [
  { city: "Fair Oaks Ranch", price: "195", path: "/fair-oaks-ranch-car-service" },
  { city: "Boerne", price: "225", path: "/boerne-car-service" },
  { city: "New Braunfels", price: "245", path: "/new-braunfels-car-service" },
  { city: "Seguin", price: "250", path: "/seguin-car-service" },
  { city: "Lakehills", price: "265", path: "/lakehills-car-service" },
  { city: "Canyon Lake", price: "285", path: "/canyon-lake-car-service" },
  { city: "Wimberley", price: "295", path: "/wimberley-car-service" },
];

const FlatRateShowcase = () => {
  const sectionRef = useRef<HTMLElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="py-16 bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950">
      <div className="container mx-auto px-4">
        
        <div className={`text-center mb-12 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="flex items-center justify-center gap-3 mb-4">
            <Plane className="w-8 h-8 text-amber-500 animate-pulse" />
            <h2 className="text-3xl sm:text-4xl font-serif font-bold text-white">
              Featured All-Inclusive Flat Rates
            </h2>
            <Plane className="w-8 h-8 text-amber-500 scale-x-[-1] animate-pulse" />
          </div>
          <p className="text-lg text-amber-500 font-medium">
            Luxury SUV Transfers to/from SAT Airport
          </p>
          <p className="text-sm text-gray-400 mt-2">
            2025 Audi Q7 Premium SUV • Professional Chauffeur
          </p>
        </div>

        {/* 6 Column Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-5 max-w-7xl mx-auto">
          {rates.map((rate, index) => (
            <Link
              key={rate.city}
              to={rate.path}
              className={`group transition-all duration-500 ease-out ${
                isVisible 
                  ? 'opacity-100 translate-y-0' 
                  : 'opacity-0 translate-y-8'
              }`}
              style={{ transitionDelay: isVisible ? `${index * 50}ms` : '0ms' }}
            >
              <div className="relative p-6 rounded-lg overflow-hidden text-center h-full transition-all duration-500 ease-out transform hover:scale-105 hover:-translate-y-1">
                {/* Gradient Background */}
                <div className="absolute inset-0 bg-gradient-to-br from-gray-800 via-gray-800 to-gray-900 transition-all duration-500" />
                
                {/* Hover Gradient Overlay */}
                <div className="absolute inset-0 bg-gradient-to-br from-amber-500/0 via-amber-500/0 to-amber-600/0 group-hover:from-amber-500/10 group-hover:via-amber-500/5 group-hover:to-amber-600/10 transition-all duration-500" />
                
                {/* Top Border Glow */}
                <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-amber-400 via-amber-500 to-amber-400 group-hover:shadow-[0_0_20px_rgba(245,158,11,0.6)] transition-all duration-300" />
                
                {/* Bottom Glow on Hover */}
                <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-3/4 h-px bg-amber-500/0 group-hover:bg-amber-500/50 group-hover:shadow-[0_0_15px_rgba(245,158,11,0.4)] transition-all duration-500" />
                
                {/* Content */}
                <div className="relative z-10">
                  <div className="text-xl font-bold text-white mb-2 h-14 flex items-center justify-center leading-tight group-hover:text-amber-50 transition-colors duration-300">
                    {rate.city}
                  </div>
                  
                  <div className="text-4xl font-black text-amber-500 mb-3 group-hover:text-amber-400 transition-colors duration-300 group-hover:drop-shadow-[0_0_8px_rgba(245,158,11,0.5)]">
                    ${rate.price}<span className="text-xl align-top">.00</span>
                  </div>
                  
                  <p className="text-xs text-gray-300 font-semibold uppercase tracking-wide">
                    All-Inclusive Price
                  </p>
                  <p className="text-xs text-gray-400 mt-1 group-hover:text-gray-300 transition-colors duration-300">
                    (Includes Gratuity & Airport Fees)
                  </p>
                </div>
                
                {/* Shadow */}
                <div className="absolute inset-0 shadow-xl group-hover:shadow-2xl group-hover:shadow-amber-500/20 rounded-lg transition-shadow duration-500 pointer-events-none" />
              </div>
            </Link>
          ))}
        </div>
        
        {/* Call to Action */}
        <div className={`text-center mt-12 transition-all duration-500 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
        }`}
        style={{ transitionDelay: isVisible ? '350ms' : '0ms' }}>
          <Button 
            className="px-8 py-6 text-lg font-bold rounded-full bg-gradient-to-r from-amber-500 to-amber-600 text-slate-950 hover:from-amber-400 hover:to-amber-500 transition-all duration-300 shadow-lg hover:shadow-amber-500/40 hover:scale-105"
            asChild
          >
            <Link to="/book">
              Reserve Your Luxury Transfer
            </Link>
          </Button>
        </div>
      </div>
    </section>
  );
};

export default FlatRateShowcase;
